/**
 * Detalle de una tarea de scraping: los precios que obtuvo el bot.
 *
 * Abre en un modal desde la tabla de «Últimas tareas» y pide
 * `GET /api/pricing/tareas/:id/resultados`, que lee el fichero de datos que dejó
 * el bot. Muestra el resumen por producto (registros, mínimo, promedio y
 * máximo) y el listado de estaciones, con filtro por producto y búsqueda por
 * estación o dirección.
 */

import { CircleX, FileSpreadsheet, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatNumber } from '@/lib/utils';

import { useResultadosTarea } from '../pricing.api';
import type { TareaPricing } from '../pricing.types';

interface DetalleTareaDialogProps {
  tarea: TareaPricing;
  onClose: () => void;
  /** Descarga el Excel de la misma tarea (lo resuelve la pantalla). */
  onDescargar: () => void;
}

/** Ubicación legible de los parámetros de la tarea. */
function ubicacionDe(tarea: TareaPricing): string {
  const { departamento, provincia, distrito } = tarea.params ?? {};
  const partes = [departamento, provincia, distrito].filter(Boolean);

  return partes.length ? partes.join(' / ') : 'Todas las ubicaciones';
}

/** Importe formateado; `—` cuando la tarea no tiene precios de ese producto. */
function monto(valor: number | null): string {
  return valor === null ? '—' : formatCurrency(valor);
}

export function DetalleTareaDialog({ tarea, onClose, onDescargar }: DetalleTareaDialogProps) {
  const resultados = useResultadosTarea(tarea.id);
  const datos = resultados.data;

  const [producto, setProducto] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const productos = useMemo(
    () => [...new Set((datos?.resumen ?? []).map((fila) => fila.producto))].sort(),
    [datos],
  );

  const precios = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();

    return (datos?.precios ?? [])
      .filter((fila) => {
        if (producto && fila.producto !== producto) return false;
        if (!termino) return true;

        return `${fila.estacion} ${fila.direccion}`.toLowerCase().includes(termino);
      })
      .sort(
        (a, b) =>
          a.producto.localeCompare(b.producto, 'es') || a.precio - b.precio,
      );
  }, [datos, producto, busqueda]);

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title="Resultados de la consulta"
      description={ubicacionDe(tarea)}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
          <Button variant="outline" onClick={onDescargar} disabled={!datos}>
            <FileSpreadsheet className="mr-1.5 size-4" aria-hidden="true" />
            Descargar Excel
          </Button>
        </>
      }
    >
      {resultados.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : resultados.error ? (
        <Alert variant="warning" title="No se pudieron leer los resultados">
          <span className={ICONO_EN_ALERTA}>
            <CircleX className="size-4" aria-hidden="true" />
            {resultados.error instanceof Error
              ? resultados.error.message
              : 'El servicio de precios no devolvió los datos de esta tarea.'}
          </span>
        </Alert>
      ) : datos ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success">{datos.tarea.estado}</Badge>
            <Badge variant="outline">{formatNumber(datos.total)} registros</Badge>
            <Badge variant="outline">Duración {datos.tarea.duracion}</Badge>
            <Badge variant="outline">
              {formatNumber(datos.tarea.intentos)}{' '}
              {datos.tarea.intentos === 1 ? 'intento' : 'intentos'}
            </Badge>
            <span className="text-xs text-ink-400">{datos.fecha}</span>
          </div>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-ink-900">Resumen por producto</h3>

            <div className="overflow-x-auto rounded-control border border-ink-100">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-ink-50 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="px-3 py-2">Categoría</th>
                    <th className="px-3 py-2">Producto</th>
                    <th className="px-3 py-2 text-right">Registros</th>
                    <th className="px-3 py-2 text-right">Mínimo</th>
                    <th className="px-3 py-2 text-right">Promedio</th>
                    <th className="px-3 py-2 text-right">Máximo</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.resumen.map((fila) => (
                    <tr
                      key={`${fila.categoria}|${fila.producto}`}
                      className="border-t border-ink-50"
                    >
                      <td className="px-3 py-2 text-ink-500">{fila.categoria}</td>
                      <td className="px-3 py-2 text-ink-800">{fila.producto}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink-500">
                        {formatNumber(fila.registros)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink-700">
                        {monto(fila.minimo)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-ink-900">
                        {monto(fila.promedio)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink-700">
                        {monto(fila.maximo)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-2">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <h3 className="text-sm font-semibold text-ink-900">
                Precios obtenidos{' '}
                <span className="font-normal text-ink-400">
                  ({formatNumber(precios.length)} de {formatNumber(datos.total)})
                </span>
              </h3>

              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={producto}
                  onChange={(evento) => setProducto(evento.target.value)}
                  className="h-9 w-full rounded-control border border-ink-200 bg-white px-3 text-left text-sm text-ink-900 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none sm:w-56"
                  aria-label="Filtrar por producto"
                >
                  <option value="">Todos los productos</option>
                  {productos.map((nombre) => (
                    <option key={nombre} value={nombre}>
                      {nombre}
                    </option>
                  ))}
                </Select>

                <div className="relative w-full sm:w-64">
                  <Search
                    className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-400"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    value={busqueda}
                    onChange={(evento) => setBusqueda(evento.target.value)}
                    placeholder="Buscar estación o dirección…"
                    aria-label="Buscar estación o dirección"
                    className="h-9 w-full rounded-control border border-ink-200 bg-white pr-3 pl-8 text-sm text-ink-900 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {precios.length === 0 ? (
              <p className="rounded-control border border-dashed border-ink-200 px-3 py-6 text-center text-sm text-ink-400">
                No hay precios que coincidan con el filtro.
              </p>
            ) : (
              <div className="max-h-80 overflow-auto rounded-control border border-ink-100">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-ink-50 text-xs font-semibold uppercase tracking-wider text-ink-400">
                      <th className="px-3 py-2">Producto</th>
                      <th className="px-3 py-2">Estación</th>
                      <th className="px-3 py-2">Dirección</th>
                      <th className="px-3 py-2">Teléfono</th>
                      <th className="px-3 py-2 text-right">Precio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {precios.map((fila, indice) => (
                      <tr
                        key={`${fila.producto}|${fila.estacion}|${indice}`}
                        className="border-t border-ink-50"
                      >
                        <td className="px-3 py-2 text-ink-500">{fila.producto}</td>
                        <td className="px-3 py-2 text-ink-800">{fila.estacion}</td>
                        <td
                          className="max-w-[280px] truncate px-3 py-2 text-ink-500"
                          title={fila.direccion}
                        >
                          {fila.direccion || '—'}
                        </td>
                        <td className="px-3 py-2 text-ink-500">{fila.telefono || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-ink-900">
                          {formatCurrency(fila.precio)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}
